import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export type VideoDisplayMode = 'grid' | 'list' | 'carousel';
export type VideoType = 'youtube' | 'vimeo' | 'dailymotion' | 'upload';

export interface Video {
  id: string;
  title: string;
  description?: string;
  url: string;
  thumbnailUrl?: string;
  type: VideoType;
  videoId?: string;
  position: number;
}

// For database records
interface VideoRecord {
  id: string;
  chatbot_public_id: string;
  title: string;
  description: string | null;
  url: string;
  thumbnail_url: string | null;
  type: string | null;
  video_id: string | null;
  position: number | null;
  created_at: string;
  updated_at: string;
}

interface VideoSettings {
  displayMode: VideoDisplayMode;
  gridColumns: number;
  spacing: number;
}

interface LoadingState {
  initialize: boolean;
  settings: boolean;
  upload: boolean;
  delete: boolean;
  reorder: boolean;
}

interface VideoSystemStore {
  settings: VideoSettings;
  videos: Video[];
  loading: LoadingState;
  error: string | null;
  initialized: boolean;
  updateSettings: (chatbotPublicId: string, settings: Partial<VideoSettings>) => Promise<void>;
  addVideo: (chatbotPublicId: string, video: Omit<Video, 'id' | 'position'>) => Promise<void>;
  updateVideo: (chatbotPublicId: string, videoId: string, updates: Partial<Omit<Video, 'id' | 'position'>>) => Promise<void>;
  deleteVideo: (chatbotPublicId: string, videoId: string) => Promise<void>;
  reorderVideos: (chatbotPublicId: string, videoIds: string[]) => Promise<void>;
  initialize: (chatbotPublicId: string) => Promise<void>;
}

const DEFAULT_SETTINGS: VideoSettings = {
  displayMode: 'grid',
  gridColumns: 3,
  spacing: 4
};

const DEFAULT_LOADING_STATE: LoadingState = {
  initialize: false,
  settings: false,
  upload: false,
  delete: false,
  reorder: false
};

export const useVideoSystem = create<VideoSystemStore>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  videos: [],
  loading: DEFAULT_LOADING_STATE,
  error: null,
  initialized: false,

  initialize: async (chatbotPublicId) => {
    try {
      set({ loading: { ...get().loading, initialize: true }, error: null });

      // Fetch settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('video_settings')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

      // Fetch videos
      const { data: videosData, error: videosError } = await supabase
        .from('videos')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('position');

      if (videosError) throw videosError;

      // Map database fields to our interface
      const mappedVideos: Video[] = videosData ? videosData.map((video: VideoRecord) => ({
        id: video.id,
        title: video.title,
        description: video.description || undefined,
        url: video.url,
        thumbnailUrl: video.thumbnail_url || undefined,
        type: (video.type as VideoType) || 'youtube',
        videoId: video.video_id || undefined,
        position: video.position || 0
      })) : [];

      // Map settings
      const mappedSettings: VideoSettings = settingsData ? {
        displayMode: (settingsData.display_mode || DEFAULT_SETTINGS.displayMode) as VideoDisplayMode,
        gridColumns: settingsData.grid_columns || DEFAULT_SETTINGS.gridColumns,
        spacing: settingsData.spacing || DEFAULT_SETTINGS.spacing
      } : DEFAULT_SETTINGS;

      set({
        settings: mappedSettings,
        videos: mappedVideos,
        initialized: true
      });
    } catch (err) {
      console.error('Error initializing videos:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error initializing videos',
        settings: DEFAULT_SETTINGS,
        videos: [],
        initialized: true
      });
    } finally {
      set({ loading: { ...get().loading, initialize: false } });
    }
  },

  updateSettings: async (chatbotPublicId, settings) => {
    try {
      set({ loading: { ...get().loading, settings: true }, error: null });

      const { data, error: checkError } = await supabase
        .from('video_settings')
        .select('id')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      // Convert from camelCase to snake_case for database
      const dbSettings = {
        ...(settings.displayMode !== undefined && { display_mode: settings.displayMode }),
        ...(settings.gridColumns !== undefined && { grid_columns: settings.gridColumns }),
        ...(settings.spacing !== undefined && { spacing: settings.spacing })
      };

      if (data) {
        // Update existing settings
        const { error } = await supabase
          .from('video_settings')
          .update(dbSettings)
          .eq('id', data.id);

        if (error) throw error;
      } else {
        // Insert new settings
        const { error } = await supabase
          .from('video_settings')
          .insert({
            chatbot_public_id: chatbotPublicId,
            ...dbSettings
          });

        if (error) throw error;
      }

      // Update local state
      set(state => ({
        settings: { ...state.settings, ...settings }
      }));
    } catch (err) {
      console.error('Error updating settings:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating settings' });
    } finally {
      set({ loading: { ...get().loading, settings: false } });
    }
  },

  addVideo: async (chatbotPublicId, video) => {
    try {
      set({ loading: { ...get().loading, upload: true }, error: null });

      // Get current max position
      const maxPosition = Math.max(0, ...get().videos.map(v => v.position));

      const { error } = await supabase
        .from('videos')
        .insert({
          chatbot_public_id: chatbotPublicId,
          title: video.title,
          description: video.description,
          url: video.url,
          thumbnail_url: video.thumbnailUrl,
          type: video.type,
          video_id: video.videoId,
          position: maxPosition + 1
        });

      if (error) throw error;

      // Refresh videos
      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error adding video:', err);
      set({ error: err instanceof Error ? err.message : 'Error adding video' });
    } finally {
      set({ loading: { ...get().loading, upload: false } });
    }
  },

  updateVideo: async (chatbotPublicId, videoId, updates) => {
    try {
      set({ loading: { ...get().loading, settings: true }, error: null });

      // Convert from camelCase to snake_case for database
      const dbUpdates = {
        ...(updates.title !== undefined && { title: updates.title }),
        ...(updates.description !== undefined && { description: updates.description }),
        ...(updates.url !== undefined && { url: updates.url }),
        ...(updates.thumbnailUrl !== undefined && { thumbnail_url: updates.thumbnailUrl }),
        ...(updates.type !== undefined && { type: updates.type }),
        ...(updates.videoId !== undefined && { video_id: updates.videoId })
      };

      const { error } = await supabase
        .from('videos')
        .update(dbUpdates)
        .eq('id', videoId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Update local state
      set(state => ({
        videos: state.videos.map(video =>
          video.id === videoId ? { ...video, ...updates } : video
        )
      }));
    } catch (err) {
      console.error('Error updating video:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating video' });
    } finally {
      set({ loading: { ...get().loading, settings: false } });
    }
  },

  deleteVideo: async (chatbotPublicId, videoId) => {
    try {
      set({ loading: { ...get().loading, delete: true }, error: null });

      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      set(state => ({
        videos: state.videos.filter(v => v.id !== videoId)
      }));
    } catch (err) {
      console.error('Error deleting video:', err);
      set({ error: err instanceof Error ? err.message : 'Error deleting video' });
    } finally {
      set({ loading: { ...get().loading, delete: false } });
    }
  },

  reorderVideos: async (chatbotPublicId, videoIds) => {
    try {
      set({ loading: { ...get().loading, reorder: true }, error: null });

      // Update positions
      for (let i = 0; i < videoIds.length; i++) {
        const { error } = await supabase
          .from('videos')
          .update({ position: i })
          .eq('id', videoIds[i])
          .eq('chatbot_public_id', chatbotPublicId);

        if (error) throw error;
      }

      // Refresh videos
      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error reordering videos:', err);
      set({ error: err instanceof Error ? err.message : 'Error reordering videos' });
    } finally {
      set({ loading: { ...get().loading, reorder: false } });
    }
  }
}));